import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Clock, BookOpen, Trash2, Edit2, CalendarDays } from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EVENT_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#ef4444', label: 'Red' },
  { value: '#22c55e', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#14b8a6', label: 'Teal' },
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', start: '', end: '', all_day: true, color: '#6366f1', type: 'event' });

  useEffect(() => {
    loadEvents();
  }, [currentMonth, currentYear]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await calendarApi.getEvents();
      setEvents(data.events || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  }, [firstDay, daysInMonth]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach(e => {
      const d = new Date(e.start);
      if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(e);
      }
    });
    return map;
  }, [events, currentMonth, currentYear]);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const goToToday = () => { setCurrentMonth(today.getMonth()); setCurrentYear(today.getFullYear()); };

  const openCreateForDay = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setForm({ title: '', description: '', start: dateStr, end: dateStr, all_day: true, color: '#6366f1', type: 'event' });
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      const event = await calendarApi.create(form);
      setEvents(p => [...p, event]);
      setShowCreate(false);
      toast.success('Event created');
    } catch (e) { toast.error(e.message); }
  };

  const handleUpdate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      await calendarApi.update(showEdit, form);
      await loadEvents();
      setShowEdit(null);
      toast.success('Event updated');
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (eventId) => {
    if (eventId.startsWith('assign_')) { toast.info('Assignment due dates are managed in Classroom'); return; }
    try {
      await calendarApi.delete(eventId);
      setEvents(p => p.filter(e => e.event_id !== eventId));
      setSelectedDay(null);
      toast.success('Event deleted');
    } catch (e) { toast.error(e.message); }
  };

  const openEdit = (event) => {
    if (event.event_id.startsWith('assign_')) { toast.info('Assignment due dates are managed in Classroom'); return; }
    setForm({ title: event.title, description: event.description || '', start: event.start?.split('T')[0] || '', end: event.end?.split('T')[0] || '', all_day: event.all_day, color: event.color || '#6366f1', type: event.type || 'event' });
    setShowEdit(event.event_id);
  };

  const selectedDayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <div className="min-h-screen bg-background" data-testid="calendar-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/suite')} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
            <CalendarDays className="w-6 h-6 text-indigo-500" />
            <h1 className="text-xl font-bold text-foreground">Calendar</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="today-btn">Today</Button>
            <Button onClick={() => { setForm({ title: '', description: '', start: '', end: '', all_day: true, color: '#6366f1', type: 'event' }); setShowCreate(true); }} data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-1" /> New Event
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-5 h-5" /></Button>
          <h2 className="text-2xl font-bold text-foreground" data-testid="current-month">{MONTHS[currentMonth]} {currentYear}</h2>
          <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-5 h-5" /></Button>
        </div>

        <div className="flex gap-6">
          {/* Calendar Grid */}
          <div className="flex-1">
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs font-semibold text-muted-foreground uppercase py-2">{d}</div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7 border-t border-l border-border">
              {calendarDays.map((day, i) => (
                <div
                  key={i}
                  onClick={() => day && setSelectedDay(day)}
                  onDoubleClick={() => day && openCreateForDay(day)}
                  className={`min-h-[100px] border-r border-b border-border p-1.5 cursor-pointer transition-colors ${
                    day ? 'hover:bg-muted/50' : 'bg-muted/20'
                  } ${selectedDay === day ? 'bg-primary/5 ring-1 ring-primary/30' : ''}`}
                  data-testid={day ? `calendar-day-${day}` : undefined}
                >
                  {day && (
                    <>
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${
                        isToday(day) ? 'bg-primary text-primary-foreground' : 'text-foreground'
                      }`}>{day}</span>
                      <div className="mt-0.5 space-y-0.5">
                        {(eventsByDay[day] || []).slice(0, 3).map(e => (
                          <div
                            key={e.event_id}
                            className="text-[10px] px-1.5 py-0.5 rounded truncate text-white font-medium"
                            style={{ backgroundColor: e.color || '#6366f1' }}
                            title={e.title}
                          >
                            {e.type === 'assignment' && <BookOpen className="w-2.5 h-2.5 inline mr-0.5" />}
                            {e.title}
                          </div>
                        ))}
                        {(eventsByDay[day] || []).length > 3 && (
                          <span className="text-[10px] text-muted-foreground pl-1">+{eventsByDay[day].length - 3} more</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Side panel - selected day events */}
          <div className="w-72 shrink-0 hidden lg:block">
            <div className="sticky top-24 rounded-xl border border-border bg-card p-4">
              <h3 className="font-semibold text-foreground mb-3">
                {selectedDay ? `${MONTHS[currentMonth]} ${selectedDay}` : 'Select a day'}
              </h3>
              {selectedDay ? (
                selectedDayEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events this day.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedDayEvents.map(e => (
                      <div key={e.event_id} className="p-2.5 rounded-lg border border-border hover:bg-muted/50 transition-colors group">
                        <div className="flex items-start gap-2">
                          <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: e.color || '#6366f1' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{e.title}</p>
                            {e.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{e.description}</p>}
                            <div className="flex items-center gap-1 mt-1">
                              {e.type === 'assignment' ? (
                                <span className="text-[10px] bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded font-medium">Assignment</span>
                              ) : (
                                <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded capitalize">{e.type}</span>
                              )}
                            </div>
                          </div>
                          {!e.event_id.startsWith('assign_') && (
                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openEdit(e)} className="p-1 hover:bg-muted rounded"><Edit2 className="w-3 h-3 text-muted-foreground" /></button>
                              <button onClick={() => handleDelete(e.event_id)} className="p-1 hover:bg-muted rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <p className="text-sm text-muted-foreground">Click on a day to view events.</p>
              )}
              {selectedDay && (
                <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => openCreateForDay(selectedDay)}>
                  <Plus className="w-3 h-3 mr-1" /> Add Event
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Events List */}
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Upcoming Events</h3>
          <div className="space-y-1">
            {events
              .filter(e => new Date(e.start) >= new Date(new Date().setHours(0, 0, 0, 0)))
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .slice(0, 10)
              .map(e => (
                <div key={e.event_id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: e.color || '#6366f1' }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{e.title}</p>
                    {e.description && <p className="text-xs text-muted-foreground truncate">{e.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Clock className="w-3 h-3" />
                    {new Date(e.start).toLocaleDateString()}
                  </div>
                  {e.type === 'assignment' && (
                    <span className="text-[10px] bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded font-medium shrink-0">Assignment</span>
                  )}
                </div>
              ))}
            {events.filter(e => new Date(e.start) >= new Date(new Date().setHours(0, 0, 0, 0))).length === 0 && (
              <p className="text-sm text-muted-foreground py-4 text-center">No upcoming events</p>
            )}
          </div>
        </div>
      </main>

      {/* Create Event Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Event title" data-testid="event-title-input" />
            <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <Input type="date" value={form.start} onChange={e => setForm(p => ({ ...p, start: e.target.value }))} data-testid="event-start-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                <Input type="date" value={form.end} onChange={e => setForm(p => ({ ...p, end: e.target.value }))} data-testid="event-end-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Type</label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="class">Class</SelectItem>
                    <SelectItem value="exam">Exam</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Color</label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {EVENT_COLORS.map(c => (
                    <button key={c.value} onClick={() => setForm(p => ({ ...p, color: c.value }))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${form.color === c.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }} title={c.label} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-event-btn">Create Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={!!showEdit} onOpenChange={() => setShowEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Event title" />
            <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description (optional)" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <Input type="date" value={form.start} onChange={e => setForm(p => ({ ...p, start: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                <Input type="date" value={form.end} onChange={e => setForm(p => ({ ...p, end: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Type</label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="class">Class</SelectItem>
                    <SelectItem value="exam">Exam</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Color</label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {EVENT_COLORS.map(c => (
                    <button key={c.value} onClick={() => setForm(p => ({ ...p, color: c.value }))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${form.color === c.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }} title={c.label} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
